export const CHANGELOG_DISMISSED_HASH_KEY = 'amazing_crm_dashboard_changelog_dismissed_hash';

export type ChangelogBlock =
  | { type: 'h1'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'li'; text: string }
  | { type: 'p'; text: string };

export interface DashboardChangelog {
  hash: string;
  blocks: ChangelogBlock[];
}

const trimBasePath = (basePath: string) => basePath.replace(/\/$/, '');

export function parseChangelogMarkdown(markdown: string): ChangelogBlock[] {
  return markdown
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line): ChangelogBlock => {
      if (line.startsWith('### ')) return { type: 'h3', text: line.slice(4).trim() };
      if (line.startsWith('## ')) return { type: 'h2', text: line.slice(3).trim() };
      if (line.startsWith('# ')) return { type: 'h1', text: line.slice(2).trim() };
      if (line.startsWith('- ')) return { type: 'li', text: line.slice(2).trim() };
      return { type: 'p', text: line };
    });
}

export function shouldShowChangelog(hash: string): boolean {
  return window.localStorage.getItem(CHANGELOG_DISMISSED_HASH_KEY) !== hash;
}

export function dismissChangelog(hash: string): void {
  window.localStorage.setItem(CHANGELOG_DISMISSED_HASH_KEY, hash);
}

export function getLatestChangelogDate(blocks: ChangelogBlock[]): string | null {
  return blocks.find((block) => block.type === 'h2')?.text ?? null;
}

export async function fetchDashboardChangelog(basePath = import.meta.env.BASE_URL): Promise<DashboardChangelog | null> {
  const base = trimBasePath(basePath);
  const metaResponse = await fetch(`${base}/crm-changelog-meta.json`, { cache: 'no-store' });
  if (!metaResponse.ok) return null;

  const meta = await metaResponse.json() as { hash?: string };
  if (!meta.hash) return null;

  const markdownResponse = await fetch(`${base}/crm-changelog.md`, { cache: 'no-store' });
  if (!markdownResponse.ok) return null;

  const markdown = await markdownResponse.text();
  const blocks = parseChangelogMarkdown(markdown);
  if (!blocks.length) return null;

  return {
    hash: meta.hash,
    blocks,
  };
}
